import { Typography } from "@material-ui/core"
import { ShareholderPage } from "api"
import Description from "common/Description"
import { useIsDesktop } from "common/MediaQueries"
import StickyImagePageLayout from "common/StickyImagePageLayout"
import { useTranslations } from "next-intl"
import { useEffect, useRef, useState } from "react"
import styled from "styled-components"
import { fontRegular } from "theme/GlobalStyle"
import SharedHoldersChart from "./SharedHoldersChart"
import ShareHoldersTable from "./ShareHoldersTable"

type ShareHolderProps = {
  shareHoldersData: ShareholderPage
}

const StyledTitle = styled(Typography).attrs({
  component: "h2",
  variant: "h2",
})<{ $showTable: boolean }>`
  font-size: 18px;
  color: ${({ theme }) => theme.colors.black};
  margin: 0 auto;
  margin-bottom: ${(p) => (p.$showTable ? `30px` : `0`)};
  ${fontRegular};

  ${(p) => p.theme.breakpoints.down("sm")} {
    margin-top: 30px;
  }
  &:focus-visible {
    border: 1px dashed white;
    outline-offset: 2px;
    outline: 2px solid black;
  }
`

const StyledChartAndTable = styled.div<{ $showTable: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 75px;

  ${(p) => p.theme.breakpoints.up("md")} {
    padding-bottom: ${(p) => (!p.$showTable ? `106px` : `40px`)};
  }
`

const StyledActualDate = styled.span`
  margin-bottom: 30px;
  font-size: 12px;

  ${(p) => p.theme.breakpoints.up("md")} {
    margin-top: 80px;
  }
`

const StyledAccessibleTable = styled.button`
  background-color: transparent;
  border: none;
  font-size: 14px;
  text-decoration: underline;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.black};
  ${fontRegular};
  font-weight: 400;
  margin-top: 15px;
`

const ShareHolders = ({ shareHoldersData }: ShareHolderProps) => {
  const headerRef = useRef<HTMLHeadingElement>()
  const [showTable, setShowTable] = useState<boolean>(false)
  const [formattedDate, setFormattedDate] = useState<string>("")
  const t = useTranslations("investor-relations")
  const isDesktop = useIsDesktop()
  const image = isDesktop
    ? shareHoldersData.shareHolder?.mediaDesktop
    : shareHoldersData.shareHolder?.mediaMobile

  const poster = isDesktop
    ? shareHoldersData.poster?.posterDesktop
    : shareHoldersData.poster?.posterMobile

  useEffect(() => {
    getDateFormatted(shareHoldersData?.shareHolder?.dbUpdateTimestamp?.value)
  }, [shareHoldersData])

  const getDateFormatted = (date) => {
    const formattedDate = new Date(date)
    const dd = formattedDate.getDate()
    const mm = formattedDate.getMonth() + 1
    const yyyy = formattedDate.getFullYear()

    setFormattedDate(`${dd}.${mm}.${yyyy}`)
  }
  const handleSwitchTables = () => {
    setTimeout(() => {
      headerRef?.current?.focus()
    }, 0)

    setShowTable((prevState) => !prevState)
  }
  return (
    <StickyImagePageLayout
      imageUrl={image?.media?.publicUrl}
      altText={image?.media?.altText}
      type={image?.media?.type}
      poster={poster?.media?.publicUrl}
    >
      <div>
        <Description
          data={{
            title: shareHoldersData.navigationTitle,
            subtitle: shareHoldersData.shareHolder?.subtitle?.value,
            description: shareHoldersData.shareHolder?.description?.value,
          }}
          parentPageType="investor-relations"
        />
        <StyledChartAndTable $showTable={showTable}>
          <StyledTitle ref={headerRef} $showTable={showTable} tabIndex={-1}>
            {t("distribution")}
          </StyledTitle>
          {!showTable && (
            <SharedHoldersChart dataSharedHolders={shareHoldersData?.details} />
          )}
          {showTable && (
            <ShareHoldersTable dataSharedHolders={shareHoldersData?.details} />
          )}
          <StyledActualDate>
            {t("lastUpdate")} {formattedDate}
          </StyledActualDate>
          {!showTable && (
            <StyledAccessibleTable onClick={handleSwitchTables}>
              {t("toAccessible")}
            </StyledAccessibleTable>
          )}

          {showTable && (
            <StyledAccessibleTable onClick={handleSwitchTables}>
              {t("backToChart")}
            </StyledAccessibleTable>
          )}
        </StyledChartAndTable>
      </div>
    </StickyImagePageLayout>
  )
}

export default ShareHolders
